import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import {
  NbMediaBreakpointsService,
  NbMenuItem,
  NbSidebarService,
  NbThemeService,
  NbMenuService,
  NbMenuBag,
} from "@nebular/theme";

import { NavigationExtras, Router } from "@angular/router";
import {
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from "@ng-bootstrap/ng-bootstrap";

import { Home } from "../../../@core/models/home";
import { AppUser } from "../../../@core/models/app.user";
import { AppUserMenu } from "../../../@core/models/app.user.menu";
import { Timeline } from "../../../@core/models/timeline";

import { LoginService } from "../../../@core/data/login.service";
import { GeneralService } from "../../../@core/data/general.service";
import { NotificationService } from "../../../@core/data/notification.service";
import { LayoutService } from "../../../@core/utils";
import { StorageService } from "../../../@core/data/storage.service";
import { MenuActionService } from "../../../@core/data/menu-action.service";
import { TimelineService } from "../../../@core/data/timeline.service";

import { HeaderFormComponent } from "./form.header.component";
import { ModalMenuComponent } from "../../../timeline/modal-menu/modal-menu.component";
import { HeaderDialogContactComponent } from "./dialog-contact.component";

import { distinctUntilChanged, map, takeUntil } from "rxjs/operators";
import { Subject, Subscription } from "rxjs";

import { generalSettings } from "../../../../main/settings";

import { environment } from "../../../../environments/environment";

declare var cast: any;

@Component({
  selector: "edm-header",
  styleUrls: ["./header.component.scss"],
  templateUrl: "./header.component.html",
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {

  pathBaseTranslate: string = "theme.components.header.";

  nbMenuBag: Subscription;

  private destroy$: Subject<void> = new Subject<void>();
  userPictureOnly: boolean = false;

  user: AppUser = null;
  home: Home = null;

  headerMenu: boolean;
  headerMenuItems: number;
  enableTimeline: boolean;
  enableSocialMedia: boolean;
  enableExplore: boolean;
  enableSearchButton: boolean;
  enableMenuLink: boolean;

  generalSettings: any;

  userMenu: NbMenuItem[] = [];
  extrasMenuItems: boolean = false;

  openingPopUp: boolean = false;

  inAppRegistration: boolean = false;
  myList: boolean = false;
  myCertificates: boolean = false;

  activeMenu!: boolean;

  ngbModalOptions: NgbModalOptions = {
    backdrop: "static",
    keyboard: false,
    size: "lg",
    container: "nb-layout",
  };

  userMenu$ = new Subscription();

  menu: AppUserMenu;

  constructor(
    private modal: NgbModal,
    private menuService: NbMenuService,
    private sidebarService: NbSidebarService,
    private themeService: NbThemeService,
    private layoutService: LayoutService,
    private breakpointService: NbMediaBreakpointsService,
    private loginService: LoginService,
    private generalService: GeneralService,
    private storageService: StorageService,
    private notificationService: NotificationService,
    private menuActionService: MenuActionService,
    private timelineService: TimelineService,
    public router: Router
  ) {
    const gs: any = generalSettings;
    this.enableTimeline = gs.enableTimeline;
    this.enableSocialMedia = gs.enableSociaMedia;
    this.enableExplore = false;
    this.enableSearchButton = gs.enableSearch;
    this.headerMenu =
      this.enableTimeline || this.enableSocialMedia || this.enableExplore;
    this.inAppRegistration = generalSettings.inAppRegistration;
    this.myList = generalSettings.myList;
    this.myCertificates = generalSettings.certificates;
    this.headerMenuItems =
      this.booleanToNumber(this.enableTimeline) +
      this.booleanToNumber(this.enableSocialMedia) +
      this.booleanToNumber(this.enableExplore);
    this.generalSettings = generalSettings;
    this.nbMenuBag = this.menuService
    .onItemClick()
    .subscribe((menu: NbMenuBag): void => {
      if (
        menu.item.data !== undefined &&
        menu.item.data.id !== undefined &&
        menu.item.data.id == "password"
      ) {
        this.openPassword();
      }
      if (
        menu.item.data !== undefined &&
        menu.item.data.id !== undefined &&
        menu.item.data.id == "contact"
      ) {
        this.openContact();
      }
    });
  }

  ngOnInit(): void {
    if (this.loginService.isAuthenticated()) {
      this.getLoggedUser();
    } else {
      this.enableMenuLink = false;
      this.generalService.home().then((home: Home): void => {
        this.home = home;
        this.syncExploreFeature();
      });
    }
    const { xl } = this.breakpointService.getBreakpointsMap();
    this.themeService
    .onMediaQueryChange()
    .pipe(
      map(([, currentBreakpoint]): boolean => currentBreakpoint.width < xl),
      takeUntil(this.destroy$)
    )
    .subscribe(
      (isLessThanXl: boolean): boolean =>
        (this.userPictureOnly = isLessThanXl)
    );
    this.loginService.user$.subscribe((item) => {
      if (item) {
        this.userMenu = [];
        this.loginService.storedLoggedUser = null;
        if (this.loginService.isAuthenticated()) {
          this.getLoggedUser();
        }
      }
    });
    this.timelineService.timeLine$
      .pipe(takeUntil(this.destroy$))
      .subscribe((tl: Timeline): void => { this.menu = tl?.menu; });
    // recarrega na troca de sessão (URL depende do token); id evita re-fetch no mesmo usuário
    this.loginService.loggedUser$
      .pipe(
        map((user: AppUser): number => user?.id ?? null),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((): void => {
        if (!this.timelineService.timeLine$.value) {
          this.timelineService.timeline().pipe(takeUntil(this.destroy$)).subscribe();
        }
      });
    // Atualiza o badge do sino silenciosamente quando as notificações são
    // marcadas como lidas (sem recarregar a rota / trocar de tela).
    this.notificationService.allRead$
      .pipe(takeUntil(this.destroy$))
      .subscribe((): void => this.clearNotificationsBadge());
    if (environment.appname == "Time Verde") this.initializeGoogleCast();
  }

  private clearNotificationsBadge(): void {
    if (this.user) {
      this.user.notifications_count = 0;
    }

    // Mantém o cache consistente para próximas navegações/recarregamentos.
    const stored: AppUser = this.storageService.restoreObject("app_logged_user");
    if (stored) {
      stored.notifications_count = 0;
      this.storageService.storeObject(stored, "app_logged_user");
    }

    // Propaga para os demais consumidores do usuário logado.
    const current: AppUser = this.loginService.loggedUser$.value;
    if (current) {
      current.notifications_count = 0;
      this.loginService.loggedUser$.next(current);
    }
  }

  getLoggedUser(): void {
    if (this.storageService.restoreObject('app_logged_user')) {
      const user: AppUser = this.storageService.restoreObject('app_logged_user')
      this.user = user;
      this.enableMenuLink = true;
      this.getHome();
    } else {
      this.loginService.loggedUser$.subscribe((loggedUser: AppUser): void => {
        if (this.exists(loggedUser)) {
          this.user = loggedUser;
          this.enableMenuLink = true;
          this.getHome();
        } else this.getHome();
      });
    }
  }

  getHome(): void {
    if (this.storageService.restoreObject('home_config')) {
      const home: Home = this.storageService.restoreObject('home_config');
      this.home = home;
      this.syncExploreFeature();
      this.generalService.store(this.home, "home", true);
      this.loadExtraMenuItems();
    } else {
      this.generalService.home().then((home: Home): void => {
        this.home = home;
        this.syncExploreFeature();
        this.generalService.store(this.home, "home", true);
        this.loadExtraMenuItems();
      });
    }
  }

  ngOnDestroy(): void {
    this.nbMenuBag.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.userMenu$?.unsubscribe();
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, "menu-sidebar");
    this.layoutService.changeLayoutSize();
    return false;
  }

  navigateHome(): boolean {
    this.router.navigate(["/"]);
    return false;
  }

  // Ao clicar em "Explorar" já estando na tela, o router ignora a navegação
  // (mesma URL) — diferente da Comunidade, que redireciona para tag/1 e
  // reinicializa. Aqui rolamos ao topo manualmente. Vindo de outra rota, o
  // SduiComponent já reseta o scroll no ngOnInit.
  onExploreNavClick(): void {
    const path: string = this.router.url.split('?')[0];
    const alreadyOnExplore: boolean = path.split('/').filter((s: string): boolean => s !== '')[0] === 'explorar';
    if (!alreadyOnExplore) return;
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
  }

  navigateLogin(): boolean {
    const extras: NavigationExtras = {
      queryParams: { redirect: encodeURIComponent(this.router.url) },
    };
    this.router.navigate(["login"], extras);
    return false;
  }

  navigateNotifications(): boolean {
    this.router.navigate(["/sdui"], {
      queryParams: { path: "user/notifications" },
    });
    return false;
  }

  isAuthenticated(): boolean {
    return this.loginService.isAuthenticated();
  }

  hasNotifications(): boolean {
    return Number(this.user?.notifications_count || 0) > 0;
  }

  isNotificationsRoute(): boolean {
    const urlTree = this.router.parseUrl(this.router.url || "");
    const primaryPath: string = "/" + (urlTree.root.children["primary"]?.segments || [])
      .map((segment) => segment.path)
      .join("/");
    const pathQuery: string = urlTree.queryParams["path"] || urlTree.queryParams["sduiUrl"] || "";

    return primaryPath.includes("/notifications") || pathQuery === "user/notifications";
  }

  headerMenuItemClass(route: string): string {
    if (this.activeMenu) return "";
    const path: string = this.router.url.split('?')[0];
    const routerPaths: string[] = path.split("/").filter((s: string): boolean => s !== '');
    if (routerPaths.length === 0) return "";
    if (route === 'produtos') {
      return routerPaths.length === 1 && routerPaths[0] === 'produtos' ? "active" : "";
    }
    return routerPaths[0] === route ? "active" : "";
  }

  isRouteActive(route: string): boolean {
    const path = this.router.url.split('?')[0];
    const routerPaths: string[] = path.split("/");
    return (
      routerPaths.length > 0 &&
      routerPaths.findIndex((x: string): boolean => x == route) > -1
    );
  }

  private loadExtraMenuItems(): void {
    if (this.home !== null && this.home !== undefined) {
      this.assemblyMenu(this.home);
    }
  }

  private assemblyMenu(model: Home): void {
    this.extrasMenuItems = true;
    this.userMenu = [];
    this.userMenu.push({
      title: this.getTranslate("profileLabel"),
      link: "perfil",
      queryParams: { from: encodeURIComponent(this.router.url) },
    });
    this.userMenu.push({
      title: this.getTranslate("changePasswordLabel"),
      data: { id: "password" },
    });
    this.userMenu.push({
      title: this.getTranslate("supportLabel"),
      link: "perfil/suporte",
      queryParams: { from: encodeURIComponent(this.router.url) },
    });
    this.userMenu.push({
      title: this.getTranslate("logout"),
      link: "logout",
    });
  }

  getTranslate(key: string): string {
    return this.generalService.getTranslate(`${this.pathBaseTranslate}${key}`);
  }

  getTimelineLabel(): string {
    return this.generalService.getTimelineLabel();
  }

  getSocialMediaLabel(): string {
    return this.generalService.getSocialMediaLabel();
  }

  getExploreLabel(): string {
    return this.generalService.getExploreLabel();
  }

  exists(object: Object): boolean {
    return object !== null && object !== undefined && object != "";
  }

  firstName(name: string): string {
    if (!name) return "";
    return name.trim().split(" ")[0];
  }

  private booleanToNumber(value: boolean): number {
    return value ? 1 : 0;
  }

  private syncExploreFeature(): void {
    this.enableExplore = this.generalService.isExploreEnabled(this.home);
    this.headerMenu =
      this.enableTimeline || this.enableSocialMedia || this.enableExplore;
    this.headerMenuItems =
      this.booleanToNumber(this.enableTimeline) +
      this.booleanToNumber(this.enableSocialMedia) +
      this.booleanToNumber(this.enableExplore);
  }

  private openPassword(): void {
    if (!this.openingPopUp) {
      this.openingPopUp = true;
      const activeModal: NgbModalRef = this.modal.open(
        HeaderFormComponent,
        { ...this.ngbModalOptions, size: undefined, windowClass: "edm-modal-compact", backdropClass: "edm-modal-compact-backdrop" }
      );
      activeModal.result.then(
        (done: AppUser | string): void => {
          if (done && done !== null) {
            if (done == "error") {
              this.openingPopUp = false;
            } else {
              this.showToastService(
                "success",
                this.getTranslate("successAlert"),
                this.getTranslate("successMessage")
              );
              this.loginService.storeToken((done as AppUser).api_token);
              this.openingPopUp = false;
            }
          }
        },
        (): void => {
          this.openingPopUp = false;
        }
      );
    }
  }

  private openContact(): void {
    if (!this.openingPopUp) {
      this.openingPopUp = true;
      const activeModal: NgbModalRef = this.modal.open(
        HeaderDialogContactComponent,
        this.ngbModalOptions
      );
      activeModal.componentInstance.contact = this.home.contact;
      activeModal.result.then(
        (): void => {
          this.openingPopUp = false;
        },
        (): void => {
          this.openingPopUp = false;
        }
      );
    }
  }

  /*
  ---------- C A S T   F U N C T I O N S ----------
  */

  private initializeGoogleCast(): void {
    const castX = window as any;
    if(castX.cast){
      let context: any = cast?.framework.CastContext.getInstance();
      let castSession: any = context?.getCurrentSession();
      if (this.exists(castSession)) {
        castSession.sendMessage("urn:x-cast:br.com.edm.cast.clientname", "verde");
        castSession.sendMessage(
          "urn:x-cast:br.com.edm.cast.readytocast",
          "Cast into the future"
        );
      }
      context?.addEventListener(
        cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        (status: { sessionState: string }): void => {
          if (
            status.sessionState == cast.framework.SessionState.SESSION_STARTED ||
            status.sessionState == cast.framework.SessionState.SESSION_RESUMED
          ) {
            var castSession = context.getCurrentSession();
            if (this.exists(castSession)) {
              castSession.sendMessage(
                "urn:x-cast:br.com.edm.cast.clientname",
                "verde"
              );
              castSession.sendMessage(
                "urn:x-cast:br.com.edm.cast.readytocast",
                "Cast into the future"
              );
            }
          }
        }
      );
    }

  }

  /*
  ---------- G E N E R A L   F U N C T I O N S ----------
  */

  isOnCommunity(): boolean {
    return this.router.url.includes('/comunidade');
  }

  searchRoute(): string {
    return this.isOnCommunity() ? '/comunidade/pesquisa' : '/buscar';
  }

  searchLabel(): string {
    return this.isOnCommunity()
      ? this.getTranslate('searchCommunity')
      : this.getTranslate('search');
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.router.navigate([this.searchRoute()]);
    }
  }

  iconTypeLayoutClass(): string {
    return generalSettings.iconsType + "-icons-type";
  }

  private showToastService(type: "success" | "warning" | "error", title?: string, message?: string): void {
    this.generalService.showToastService(type, message, title);
  }

  isNavbarMode(): boolean {
    return !this.menuActionService.isHomeBlock(this.menu);
  }

  menuLabel(): string {
    if (this.menu?.menuitems?.length === 1) {
      return (this.menu.menuitems[0]?.cta_caption ?? '').substring(0, 16);
    }
    return this.getTranslate('menu');
  }

  public openModalMenu(): void {
    if (this.menuActionService.hasSingleItem(this.menu)) {
      this.menuActionService.openOrPlay(this.menu.menuitems[0]);
      return;
    }
    this.activeMenu = true;
    const menuModalOptions: NgbModalOptions = {
      ...this.ngbModalOptions,
      windowClass: 'menu-dropdown-modal',
      backdropClass: 'menu-dropdown-backdrop',
      backdrop: true,
      keyboard: true,
    };
    const activeModal: NgbModalRef = this.modal.open(
      ModalMenuComponent,
      menuModalOptions,
    );
    activeModal.componentInstance.menu = this.menu;
    activeModal.result.then(
      (): void => {
        this.activeMenu = false;
      },
      (): void => {
        this.activeMenu = false;
      },
    );
  }
}
